/**
 * Unified Property Service
 * Modern implementation replacing the legacy property service
 * Demonstrates advanced patterns: caching, validation, performance monitoring
 */

import {
  PropertyParams,
  PropertySlugParams,
  PropertyUrlParams,
  MainParams,
  DevelopersApiResponse,
  Category,
} from "@/types/property.types";

import { BaseService } from "./core/base-service";
import { ValidationError, NotFoundError } from "./core/errors";
import { serviceFactory, createServiceConfig } from "./core/service-factory";
import { ServiceConfig, QueryParams, ValidationSchema } from "./core/types";

// Property-specific types (imported from existing types)

export interface PropertyResponse {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  area: number;
  location: {
    city: string;
    district?: string;
    address: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  images: string[];
  amenities: Record<string, boolean>;
  developer?: {
    id: string;
    name: string;
    logo?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PropertySearchParams extends QueryParams {
  category?: Category;
  city?: string;
  priceMin?: number;
  priceMax?: number;
  areaMin?: number;
  areaMax?: number;
  propertyType?: string;
  objectType?: string;
  // Add more search parameters as needed
}

export interface PropertyListResponse {
  data: PropertyResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters?: {
    applied: Record<string, any>;
    available: Record<string, any[]>;
  };
}

/**
 * Modern property service with unified patterns
 */
export class UnifiedPropertyService extends BaseService {
  constructor(config?: Partial<ServiceConfig>) {
    const serviceConfig = createServiceConfig("property")
      .baseURL(process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:8000")
      .timeout(
        process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_ENV === "development"
          ? parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || process.env.API_TIMEOUT || "1500") || 1500
          : 80000 // Production timeout for property searches
      )
      .retries(3)
      .retryDelay(1000)
      .caching(true, 5 * 60 * 1000, "cache-first") // 5 minutes cache for property data
      .logging(true)
      .metrics(true)
      .headers({
        "Content-Type": "application/json",
      })
      .credentials("include")
      .build();

    super({ ...serviceConfig, ...config });
  }

  /**
   * Validation schemas for property operations
   */
  private static readonly schemas = {
    propertyParams: {
      rules: [
        { field: "propertyId", required: true, type: "string", min: 1 },
        { field: "dealType", type: "string" },
      ],
      allowUnknown: false,
    } as ValidationSchema,

    searchParams: {
      rules: [
        { field: "category", type: "string" },
        { field: "page", type: "number", min: 1 },
        { field: "limit", type: "number", min: 1, max: 100 },
        { field: "priceMin", type: "number", min: 0 },
        { field: "priceMax", type: "number", min: 0 },
        { field: "areaMin", type: "number", min: 0 },
        { field: "areaMax", type: "number", min: 0 },
        {
          field: "priceMax",
          custom: (value: any, data: any) => {
            if (data.priceMin && value && value < data.priceMin) {
              return "Maximum price cannot be less than minimum price";
            }
            return true;
          },
        },
        {
          field: "areaMax",
          custom: (value: any, data: any) => {
            if (data.areaMin && value && value < data.areaMin) {
              return "Maximum area cannot be less than minimum area";
            }
            return true;
          },
        },
      ],
      allowUnknown: true, // Allow additional search filters
    } as ValidationSchema,

    slugParams: {
      rules: [
        { field: "slug", required: true, type: "string", min: 1 },
        { field: "dealType", type: "string" },
      ],
      allowUnknown: false,
    } as ValidationSchema,
  };

  /**
   * Get property details by ID
   */
  async getProperty(params: PropertyParams): Promise<PropertyResponse> {
    this.validateSchema(params, UnifiedPropertyService.schemas.propertyParams);

    const cacheKey = `property-${params.propertyId}-${params.dealType || "any"}`;

    try {
      const response = await this.get<PropertyResponse>(
        "/property/property-info",
        params,
        {
          cache: true,
          cacheTtl: 10 * 60 * 1000, // 10 minutes for individual properties
          cacheTags: [`property-${params.propertyId}`, "properties"],
          timeout: 8000,
        },
      );

      this.logger.info("Property retrieved successfully", {
        propertyId: params.propertyId,
        dealType: params.dealType,
      });

      return response;
    } catch (error) {
      this.logger.warn("Failed to retrieve property", {
        propertyId: params.propertyId,
        dealType: params.dealType,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      // Enhance error with context
      if (error instanceof Error && error.message.includes("404")) {
        throw new NotFoundError("Property", params.propertyId, {
          service: this.name,
          details: { params },
        });
      }

      throw error;
    }
  }

  /**
   * Get property by slug (SEO-friendly URL)
   */
  async getPropertyBySlug(
    params: PropertySlugParams,
  ): Promise<PropertyResponse> {
    this.validateSchema(params, UnifiedPropertyService.schemas.slugParams);

    try {
      const response = await this.get<PropertyResponse>(
        "/property/find-by-slug",
        params,
        {
          cache: true,
          cacheTtl: 15 * 60 * 1000, // 15 minutes for slug-based lookups
          cacheTags: [`property-slug-${params.slug}`, "properties"],
          timeout: 8000,
        },
      );

      this.logger.info("Property retrieved by slug", {
        slug: params.slug,
        propertyId: response.id,
      });

      return response;
    } catch (error) {
      this.logger.warn("Failed to retrieve property by slug", {
        slug: params.slug,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      if (error instanceof Error && error.message.includes("404")) {
        throw new NotFoundError("Property", params.slug, {
          service: this.name,
          details: { params, searchBy: "slug" },
        });
      }

      throw error;
    }
  }

  /**
   * Get property by legacy URL (backward compatibility)
   */
  async getPropertyByUrl(params: PropertyUrlParams): Promise<PropertyResponse> {
    this.validateRequired(params, ["propertyUrl"]);

    try {
      const response = await this.get<PropertyResponse>(
        "/property/property-info-url",
        params,
        {
          cache: true,
          cacheTtl: 10 * 60 * 1000,
          cacheTags: [`property-url-${params.propertyUrl}`, "properties"],
          timeout: 8000,
        },
      );

      this.logger.info("Property retrieved by URL", {
        propertyUrl: params.propertyUrl,
        propertyId: response.id,
      });

      return response;
    } catch (error) {
      this.logger.warn("Failed to retrieve property by URL", {
        propertyUrl: params.propertyUrl,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      if (error instanceof Error && error.message.includes("404")) {
        throw new NotFoundError("Property", params.propertyUrl, {
          service: this.name,
          details: { params, searchBy: "url" },
        });
      }

      throw error;
    }
  }

  /**
   * Search and list properties with advanced filtering
   */
  async getAllProperties(
    params: PropertySearchParams = {},
  ): Promise<PropertyListResponse> {
    // Set defaults and validate
    const searchParams = {
      page: 1,
      limit: parseInt(process.env.PAGE_SIZE || "12", 10),
      ...params,
    };

    this.validateSchema(
      searchParams,
      UnifiedPropertyService.schemas.searchParams,
    );

    const cacheKey = this.generateSearchCacheKey(searchParams);

    try {
      const response = await this.get<PropertyListResponse>(
        "/property/all-property",
        searchParams,
        {
          cache: true,
          cacheTtl: 3 * 60 * 1000, // 3 minutes for search results
          cacheTags: [
            "property-search",
            `category-${searchParams.category || "all"}`,
          ],
          timeout: 8000,
        },
      );

      this.logger.info("Properties search completed", {
        params: this.sanitizeSearchParams(searchParams),
        resultCount: response.data.length,
        total: response.pagination.total,
      });

      return response;
    } catch (error) {
      this.logger.warn("Property search failed", {
        params: this.sanitizeSearchParams(searchParams),
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  }

  /**
   * Get recommended properties
   */
  async getRecommendedProperties(limit?: number): Promise<PropertyResponse[]> {
    const params = limit ? { limit } : undefined;

    try {
      const response = await this.get<PropertyResponse[]>(
        "/property/recommended-property",
        params,
        {
          cache: true,
          cacheTtl: 10 * 60 * 1000, // 10 minutes for recommendations
          cacheTags: ["recommended-properties"],
          timeout: 8000,
        },
      );

      this.logger.info("Recommended properties retrieved", {
        count: response.length,
        limit,
      });

      return response;
    } catch (error) {
      this.logger.warn("Failed to retrieve recommended properties", {
        limit,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  }

  /**
   * Get property count for a category
   */
  async getPropertyCount(category: string): Promise<{ total: number }> {
    this.validateRequired({ category }, ["category"]);

    try {
      const response = await this.get<PropertyListResponse>(
        "/property/all-property",
        {
          category,
          limit: 1,
          page: 1,
        },
        {
          cache: true,
          cacheTtl: 15 * 60 * 1000, // 15 minutes for counts
          cacheTags: [`count-${category}`, "property-counts"],
          timeout: 8000,
        },
      );

      const total = response.pagination.total;

      this.logger.debug("Property count retrieved", {
        category,
        total,
      });

      return { total };
    } catch (error) {
      this.logger.warn("Failed to retrieve property count", {
        category,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      // Return 0 on error instead of throwing
      return { total: 0 };
    }
  }

  /**
   * Get property statistics
   */
  async getPropertyStats(): Promise<{
    totalProperties: number;
    byCategory: Record<string, number>;
    byCity: Record<string, number>;
    priceRanges: {
      min: number;
      max: number;
      average: number;
    };
  }> {
    try {
      const response = await this.get<any>("/property/statistics", undefined, {
        cache: true,
        cacheTtl: 30 * 60 * 1000, // 30 minutes for statistics
        cacheTags: ["property-stats"],
        timeout: 8000,
      });

      this.logger.debug("Property statistics retrieved");
      return response;
    } catch (error) {
      this.logger.warn("Failed to retrieve property statistics", {
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  }

  /**
   * Clear property-related cache
   */
  async clearPropertyCache(pattern?: string): Promise<void> {
    const cachePattern = pattern || "property";
    await this.clearCache(cachePattern);

    this.logger.info("Property cache cleared", { pattern: cachePattern });
  }

  /**
   * Refresh property data for a specific property
   */
  async refreshProperty(propertyId: string): Promise<PropertyResponse> {
    // Clear cache for this property
    await this.clearCache(`property-${propertyId}`);

    // Fetch fresh data
    return this.getProperty({ propertyId });
  }

  /**
   * Generate cache key for search parameters
   */
  private generateSearchCacheKey(params: PropertySearchParams): string {
    const relevantParams = {
      category: params.category,
      page: params.page,
      limit: params.limit,
      city: params.city,
      priceMin: params.priceMin,
      priceMax: params.priceMax,
      areaMin: params.areaMin,
      areaMax: params.areaMax,
      propertyType: params.propertyType,
      objectType: params.objectType,
    };

    // Remove undefined values and sort for consistent keys
    const sortedParams = Object.fromEntries(
      Object.entries(relevantParams)
        .filter(([_, value]) => value !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)),
    );

    return `search-${JSON.stringify(sortedParams)}`;
  }

  /**
   * Sanitize search parameters for logging (remove sensitive data)
   */
  private sanitizeSearchParams(
    params: PropertySearchParams,
  ): Record<string, any> {
    const { ...safeParams } = params;
    return safeParams;
  }

  /**
   * Enhanced health check for property service
   */
  async healthCheck(): Promise<{
    status: "healthy" | "unhealthy";
    details?: any;
  }> {
    try {
      // Test basic connectivity with a lightweight request
      const countResult = await this.getPropertyCount("sale");

      return {
        status: "healthy",
        details: {
          service: this.name,
          endpointResponsive: true,
          samplePropertyCount: countResult.total,
          timestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      return {
        status: "unhealthy",
        details: {
          service: this.name,
          endpointResponsive: false,
          error: error instanceof Error ? error.message : "Unknown error",
          timestamp: new Date().toISOString(),
        },
      };
    }
  }
}

// Register the property service with the factory
serviceFactory.registerConstructor("unified-property", UnifiedPropertyService);

// Create and register a default instance
export const unifiedPropertyService = new UnifiedPropertyService();
serviceFactory.register(unifiedPropertyService);

// Export for backward compatibility and direct usage
export default unifiedPropertyService;
